<template>
  <div class="school-card">
    <!-- ── IMAGE DE FOND (GAUCHE) ─────────────────────────────────────────────── -->
    <div class="school-card__background">
      <PrepeersImage
        v-if="props.school.BackgroundImage"
        :src="props.school.BackgroundImage"
        :alt="`${props.school.Name} background`"
        preset="cardCover"
        image-class="school-card__bg-image"
        sizes="100vw md:400px"
        loading="lazy" />
    </div>

    <!-- ── LOGO (CHEVAUCHANT) ─────────────────────────────────────────────── -->
    <div class="school-card__logo">
      <PrepeersEntityLogo
        :logo-pict="logoPict"
        :entity-name="props.school.Name"
        variant="card"
        :alt="`${props.school.Name} logo`" />
    </div>

    <!-- ── CONTENU (DROITE) ─────────────────────────────────────────────── -->
    <div class="school-card__content">
      <!-- Titre école -->
      <h3 class="school-card__name">{{ props.school.Name }}</h3>

      <!-- Type école -->
      <div class="school-card__info">
        <span
          class="school-card__icon-slot"
          aria-hidden="true">
          <img
            src="/icons/school-card/icon-sales.svg"
            alt=""
            class="school-card__icon school-card__icon--sales" />
        </span>
        <span class="school-card__type">{{ props.school.Type }}</span>
      </div>

      <!-- Nombre de formations -->
      <div
        v-if="props.school.ProgramsCount"
        class="school-card__info">
        <span
          class="school-card__icon-slot"
          aria-hidden="true">
          <img
            src="/icons/school-card/icon-diploma.svg"
            alt=""
            class="school-card__icon school-card__icon--diploma" />
        </span>
        <span class="school-card__formations"
          >{{ props.school.ProgramsCount }} formation{{
            (props.school.ProgramsCount ?? 0) > 1 ? 's' : ''
          }}</span
        >
      </div>

      <!-- Localisations -->
      <div
        v-if="props.school.Locations && props.school.Locations.length"
        class="school-card__info">
        <span
          class="school-card__icon-slot"
          aria-hidden="true">
          <img
            src="/icons/school-card/icon-location-pin.svg"
            alt=""
            class="school-card__icon school-card__icon--pin" />
        </span>
        <span class="school-card__locations">{{ props.school.Locations.join(', ') }}</span>
      </div>

      <!-- Bouton -->
      <NuxtLink
        :to="props.school.Link"
        class="school-card__btn"
        @click="onConsultClick">
        Consulter
      </NuxtLink>
    </div>

    <!-- ── ACCENT BORDER (orange right) ─────────────────────────────────────────────── -->
    <div class="school-card__accent" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import PrepeersEntityLogo from '~/components/core/PrepeersEntityLogo.vue'
import PrepeersImage from '~/components/core/PrepeersImage.vue'
import { EMPTY_COVER_PLACEHOLDER } from '~/constants/imagePlaceholders'

interface SchoolCardProps {
  school: {
    id: number | string
    Name: string
    Type: string
    Locations: readonly string[]
    ProgramsCount?: number
    BackgroundImage?: string
    LogoImage?: string
    Link: string
  }
}

const props = withDefaults(defineProps<SchoolCardProps>(), {})

const emit = defineEmits<{
  consult: []
}>()

/**
 * @returns {void}
 */
function onConsultClick(): void {
  emit('consult')
}

/** Logo API utilisable (exclut le placeholder de couverture réservé au fond). */
const logoPict: ComputedRef<string> = computed((): string => {
  const raw: string | undefined = props.school.LogoImage?.trim()
  if (!raw || raw === EMPTY_COVER_PLACEHOLDER) return ''
  return raw
})
</script>

<style scoped>
/* ════════════════════════════════════════════════════════════
   SCHOOL CARD COMPONENT - LAYOUT HORIZONTAL
   ════════════════════════════════════════════════════════════ */

.school-card {
  position: relative;
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 200px;
  background: linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-elevated) 100%);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: all var(--duration-base) var(--ease-out);
}

/* prettier-ignore */
.school-card:hover {
  border-color: var(--accent-orange);
  box-shadow: var(--shadow-lg), 0 0 20px rgba(249, 115, 22, 0.25);
  transform: translateY(-4px);
}

/* ── BACKGROUND IMAGE (GAUCHE) ─────────────────────────────────────────────── */
.school-card__background {
  position: relative;
  flex-shrink: 0;
  width: 90px;
  height: auto;
  min-height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(3, 160, 241, 0.1) 0%, rgba(249, 115, 22, 0.05) 100%);
}

.school-card__bg-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.6;
}

/* ── LOGO (CHEVAUCHANT AU CENTRE) ─────────────────────────────────────────────── */
.school-card__logo {
  position: absolute;
  left: 90px;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  /* background: var(--bg-surface);
  border: 3px solid var(--bg-elevated);
  border-radius: var(--radius-md); */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  /* box-shadow: var(--shadow-md); */
}

.school-card__logo-image {
  width: 56px;
  height: 56px;
  object-fit: contain;
}

/* ── CONTENU (DROITE) ─────────────────────────────────────────────── */
.school-card__content {
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: var(--space-6);
  padding-left: var(--space-12);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  position: relative;
  z-index: 1;
  justify-content: flex-start;
}

/* Titre école */
.school-card__name {
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: var(--leading-snug);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Infos (type, localisation, formations) */
.school-card__info {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  min-width: 0;
}

.school-card__icon-slot {
  flex: 0 0 17px;
  width: 17px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.school-card__icon {
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

/* Même hauteur visuelle (~15px) pour les 3 pictos */
.school-card__icon--sales {
  width: 16px;
  height: 15px;
}

.school-card__icon--diploma {
  width: 15px;
  height: 15px;
}

.school-card__icon--pin {
  width: 12px;
  height: 15px;
}

.school-card__type,
.school-card__locations,
.school-card__formations {
  line-height: var(--leading-snug);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* ── BOUTON ─────────────────────────────────────────────── */
.school-card__btn {
  align-self: flex-start;
  margin-top: auto;
  padding: var(--space-2) var(--space-4);
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: 600;
  text-decoration: none;
  transition: all var(--duration-fast) var(--ease-out);
  cursor: pointer;
}

.school-card:hover .school-card__btn {
  border-color: var(--accent-orange);
  color: var(--accent-orange);
  background: rgba(249, 115, 22, 0.1);
}

/* ── ACCENT BORDER (orange right) ─────────────────────────────────────────────── */
.school-card__accent {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 4px;
  background: var(--gradient-orange);
  transition: width var(--duration-base) var(--ease-out);
}

.school-card:hover .school-card__accent {
  width: 6px;
}

/* ── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 768px) {
  .school-card {
    flex-direction: column;
    min-height: auto;
  }

  .school-card__background {
    width: 100%;
    max-width: none;
    height: 140px;
    min-height: 140px;
  }

  .school-card__logo {
    left: 50%;
    top: 140px;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
  }

  .school-card__logo-image {
    width: 48px;
    height: 48px;
  }

  .school-card__content {
    padding: var(--space-10) var(--space-5) var(--space-5) var(--space-5);
    gap: var(--space-3);
  }

  .school-card__name {
    font-size: var(--text-base);
    margin-top: var(--space-1);
  }

  .school-card__info {
    font-size: var(--text-xs);
  }

  .school-card__btn {
    margin-top: var(--space-4);
  }
}

@media (max-width: 640px) {
  .school-card__background {
    height: 100px;
    min-height: 100px;
  }

  .school-card__logo {
    top: 100px;
    width: 50px;
    height: 50px;
  }

  .school-card__logo-image {
    width: 40px;
    height: 40px;
  }

  .school-card__content {
    padding: var(--space-8) var(--space-4) var(--space-4) var(--space-4);
  }

  .school-card__name {
    font-size: var(--text-sm);
  }
}
</style>
